import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { catchError, filter, takeUntil } from 'rxjs/operators';

import { AuthService } from '@auth/auth.service';
import { SigninData } from '@auth/shared/signin-data.interface';
import { Login, ReloginStatus } from '@auth/store/auth.actions';
import { DataService } from '@data/data.service';

import { ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';

import { credentials } from '@environment';

@Component({
  selector: 'app-session-timeout-dialog',
  templateUrl: './session-timeout-dialog.component.html',
  styleUrls: ['./session-timeout-dialog.component.scss'],
  providers: [ConfirmationService],
})
export class SessionTimeoutDialogComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();

  @ViewChild('messageDialog') messageDialog: ConfirmDialog;

  display: boolean;
  header: string = '';

  private prevScreenOffset: number;

  previousUsername: string = '';

  form: FormGroup;

  constructor(
    public dataService: DataService,
    public authService: AuthService,
    private store: Store,
    private confirmationService: ConfirmationService
  ) {
    this.form = new FormGroup({
      username: new FormControl(''),
      password: new FormControl(''),
    });
  }

  ngOnInit() {
    this.authService.reloginDialog$.pipe(takeUntil(this.destroy$)).subscribe((display) => {
      this.display = display;
    });

    this.authService.reloginDialog$
      .pipe(
        filter((x) => !!x),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.init());
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  closeModal = (status = 'inProgress') => {
    if (status === 'success') {
      this.dataService.wsScreenAdjust.next(this.prevScreenOffset);
    }

    this.store.dispatch(new ReloginStatus(status));
    this.authService.reloginDialog$.next(false);
    this.dataService.hideScreen$.next(false);
    this.dataService.showSubLoadingIndicator$.next(false);
    this.dataService.showReportParametersDialog$.next(false);
  };

  init() {
    this.authService.user$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      this.previousUsername = user;
    });

    this.prevScreenOffset = this.dataService.screenOffset;
    this.dataService.hideScreen$.next(true);
  }

  login() {
    if (!this.form.get('username').value || !this.form.get('password').value) {
      this.showMessage({ title: 'Error', message: 'Username and password are required' });
      return;
    }

    if (this.form.get('username').value.toLowerCase() !== this.previousUsername.toLowerCase()) {
      this.showMessage({ title: 'Error', message: 'Cannot sign in as a different user' });
      return;
    }

    this.authService
      .login(this.form.get('username').value, this.form.get('password').value)
      .pipe(
        catchError((error: any) => {
          const errorMessage = error.error?.error?.message ?? 'Server Error';
          this.showMessage({ title: 'Error', message: `Login failed: ${errorMessage}` });
          return [];
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((data: SigninData) => {
        this.form.get('username').setValue('');
        this.form.get('password').setValue('');

        this.closeModal('success');

        this.store.dispatch(new Login(data));
      });
  }

  automaticLogin = () => {
    this.form.get('username').setValue(credentials.username);
    this.form.get('password').setValue(credentials.password);
  };

  showMessage(options: any) {
    this.confirmationService.confirm({
      key: 'reauth-message',
      header: options.title,
      message: options.message,
      accept: () => {},
    });
  }
}
